import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  contentChild,
  effect,
  ElementRef,
  Host,
  HostBinding,
  inject,
  input,
  model,
  Optional,
  SkipSelf,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { fromEvent } from 'rxjs';
import { ExpandableCardHeaderComponent } from './expandable-card-slots/expandable-card-header.component';

@Component({
  selector: 'shared-core-expandable-card, [sharedCoreExpandableCard]',
  imports: [NgClass],
  template: `
    <ng-content select="shared-core-expandable-card-header" />
    <div
      class="grid duration-200 ease-in-out"
      [ngClass]="isExpanded() ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'">
      <ng-content select="shared-core-expandable-card-content" />
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExpandableCardComponent implements AfterContentInit {
  readonly isExpanded = model(false);
  readonly toggleArea = input<'full' | 'header-only' | 'custom'>('full');

  readonly toggleElement = input<HTMLElement>();

  childrenViews: ExpandableCardComponent[] = [];

  @HostBinding('class')
  transitionClass = 'transition-[grid-rows]';

  private readonly wrapper = inject(ElementRef<HTMLElement>);
  private readonly header = contentChild.required(ExpandableCardHeaderComponent, {
    read: ElementRef,
  });

  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(@Host() @Optional() @SkipSelf() parent?: ExpandableCardComponent) {
    if (parent) {
      parent.childrenViews.push(this);
    }

    effect(() => {
      if (!this.isExpanded()) {
        this.childrenViews.forEach((child) => child.collapse());
      }
    });
  }

  ngAfterContentInit() {
    fromEvent<MouseEvent>(this.getToggleElement(), 'click').subscribe((event) =>
      this.onToggleClicked(event),
    );
  }

  private getToggleElement() {
    const toggleArea = this.toggleArea();
    if (toggleArea === 'custom') {
      const toggleElement = this.toggleElement();
      if (!toggleElement) {
        throw new Error("For toggleArea 'custom', a toggleElement must be provided.");
      }

      return toggleElement;
    }

    if (toggleArea === 'header-only') {
      return this.header().nativeElement;
    }

    return this.wrapper.nativeElement;
  }

  collapse() {
    this.isExpanded.set(false);
  }

  toggleIsExpanded() {
    this.isExpanded.update((bool) => !bool);
  }

  onToggleClicked(event: MouseEvent) {
    this.toggleIsExpanded();
    event.stopPropagation();
  }
}
